import { Alert, Badge, Card, Toggle } from '@shared/components';
import { SETTLED_VAT_NOTE, hasSettledSplit, type StatementRowView, type StatementView } from '../../utils/statementRows';
import { AgreementStatementDataTable } from './AgreementStatementDataTable';
import { ScheduledPaymentsNotice } from './ScheduledPaymentsNotice';

interface Props {
    view: StatementView;
    rows: StatementRowView[];
    futureCount: number;
    showUnreconciled: boolean;
    onShowUnreconciledChange: (next: boolean) => void;
}

const headingId = 'agreement-transactions-heading';
const toggleId = 'agreement-transactions-show-unreconciled';
const countId = 'agreement-transactions-future-count';
const noteClass = 'text-sm text-mp-warning';
const mutedClass = 'text-sm text-mp-muted';

export function StatementTransactions({ view, rows, futureCount, showUnreconciled, onShowUnreconciledChange }: Props) {
    const hasRows = view.rows.length > 0;

    return (
        <Card className="overflow-hidden">
            <section aria-labelledby={headingId}>
                <div className="space-y-3 px-6 py-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <h3 id={headingId} className="text-lg font-semibold text-mp-foreground">
                            Transactions
                        </h3>
                        {hasRows && (
                            <div className="flex items-center gap-3">
                                <Toggle
                                    id={toggleId}
                                    label="Show unreconciled"
                                    checked={showUnreconciled}
                                    onChange={onShowUnreconciledChange}
                                    ariaDescribedBy={countId}
                                />
                                <Badge variant="outline">
                                    <span aria-hidden="true">{futureCount}</span>
                                    <span id={countId} className="sr-only">
                                        {futureCount} future scheduled transactions
                                    </span>
                                </Badge>
                            </div>
                        )}
                    </div>
                    {view.degraded && <p className={noteClass}>Some transaction types are temporarily shown as Other</p>}
                    {view.truncated && <p className={noteClass}>This statement shows the first 1,000 ledger entries</p>}
                    {view.scheduledPaymentsUnavailable && <ScheduledPaymentsNotice />}
                </div>
                <TransactionsBody view={view} rows={rows} futureCount={futureCount} />
            </section>
        </Card>
    );
}

function TransactionsBody({ view, rows, futureCount }: Pick<Props, 'view' | 'rows' | 'futureCount'>) {
    if (view.rows.length === 0) return <p className={`px-6 pb-5 ${mutedClass}`}>No money has moved on this agreement yet</p>;
    if (rows.length === 0) {
        return (
            <div className="px-6 pb-5">
                <Alert variant="info" role="note">
                    Nothing is due yet. Turn on Show unreconciled to see {futureCount} scheduled transactions
                </Alert>
            </div>
        );
    }

    return (
        <>
            <AgreementStatementDataTable rows={rows} asOf={view.asOf} currency={view.currency} showType={view.detail} />
            {hasSettledSplit(rows) && <p className={`px-6 py-4 ${mutedClass}`}>{SETTLED_VAT_NOTE}</p>}
        </>
    );
}
